import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

/**
 * Fab - Floating Action Button for mobile upload
 * 
 * Features:
 * - Mobile-only (hidden on desktop via md:hidden)
 * - Only visible when authenticated
 * - Hidden on /upload route and when conflicting modals are active
 * - Hidden when text inputs are focused (to avoid obscuring Post buttons)
 * - Accessible with aria-label and keyboard focus
 * - Respects prefers-reduced-motion
 * - Safe-area padding for iOS notch
 */
const Fab = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isInputFocused, setIsInputFocused] = useState(false);
  
  // Hide FAB when any text input/textarea is focused (user is typing)
  useEffect(() => {
    const handleFocusIn = (e) => {
      const tag = e.target.tagName?.toLowerCase();
      const type = e.target.type?.toLowerCase();
      // Hide for text inputs, textareas, and contenteditable elements
      if (
        tag === 'textarea' ||
        (tag === 'input' && ['text', 'search', 'email', 'url', 'tel'].includes(type)) ||
        e.target.isContentEditable
      ) {
        setIsInputFocused(true);
      }
    };
    
    const handleFocusOut = (e) => {
      const tag = e.target.tagName?.toLowerCase();
      const type = e.target.type?.toLowerCase();
      if (
        tag === 'textarea' ||
        (tag === 'input' && ['text', 'search', 'email', 'url', 'tel'].includes(type)) ||
        e.target.isContentEditable
      ) {
        // Small delay to handle focus moving between inputs
        setTimeout(() => {
          const activeTag = document.activeElement?.tagName?.toLowerCase();
          const activeType = document.activeElement?.type?.toLowerCase();
          const stillInInput = 
            activeTag === 'textarea' ||
            (activeTag === 'input' && ['text', 'search', 'email', 'url', 'tel'].includes(activeType)) ||
            document.activeElement?.isContentEditable;
          if (!stillInInput) {
            setIsInputFocused(false);
          }
        }, 100);
      }
    };
    
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    
    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, []);
  
  // Don't show FAB if not authenticated
  if (!user) return null;
  
  // Don't show FAB on upload page
  if (location.pathname === '/upload') return null;

  // Don't show FAB while in Search map mode — it overlaps map/list content
  const searchParams = new URLSearchParams(location.search);
  if (location.pathname === '/search' && searchParams.get('view') === 'map') return null;
  
  // Don't show FAB when user is typing (input focused)
  if (isInputFocused) return null;
  
  const handleClick = () => {
    navigate('/upload');
  };
  
  return (
    <button
      onClick={handleClick}
      className="fixed bottom-6 right-6 z-40 w-14 h-14 bg-passion-red text-white rounded-full shadow-lg hover:shadow-xl hover:bg-red-600 active:scale-95 transition-all duration-200 flex items-center justify-center md:hidden focus:outline-none focus:ring-4 focus:ring-passion-red focus:ring-opacity-50"
      aria-label="Upload a sandwich"
      style={{
        paddingBottom: 'env(safe-area-inset-bottom)',
        marginBottom: 'env(safe-area-inset-bottom)'
      }}
    >
      <Plus size={28} strokeWidth={2.5} aria-hidden="true" />
    </button>
  );
};

export default Fab;

